import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash-es';

import { Input } from 'components/styled-primitives';
import ModalInputSection from '../../components/modal-input-section';

import fetchUsersByEmailPartial from '../../actions/fetch-users-by-email-partial';

interface EmailLookupItem {
  label?: string;
  id?: string;
  primaryEmail: string;
  countryCode?: string;
  phantom?: boolean;
}

interface FetchListParams {
  value: string;
  showPhantomUsers: boolean;
  setItems: React.Dispatch<React.SetStateAction<EmailLookupItem[]>>;
  allowInvite: boolean;
}

// HELPERS - debounce doesn't work correctly inside a component
const fetchList = async ({
  value,
  showPhantomUsers,
  setItems,
  allowInvite,
}: FetchListParams): Promise<void> => {
  const result = await fetchUsersByEmailPartial(value);

  if ('succeeded' in result && result.succeeded) {
    const newUser: EmailLookupItem = {
      label: `<<Create New User>> (${value})`,
      id: 'CREATE',
      primaryEmail: value,
      countryCode: 'US',
    };
    // `data` is a boundary `any` from the wrapped HTTP result / JSON.parse
    const items = (Object.values(result.data) as EmailLookupItem[]).filter(
      (i) => showPhantomUsers || i.phantom === false,
    );

    allowInvite && items.push(newUser);
    setItems(items);
  } else if ('failed' in result && result.failed) {
    setItems([]);
    console.error('request failed', result.error);
  }
};
const fetchListDebounced = debounce(fetchList, 500);

interface EmailLookupProps {
  sectionTitle?: React.ReactNode;
  initialValue?: string;
  onSelect: (item: EmailLookupItem | undefined) => void;
  resetValue?: boolean;
  showPhantomUsers?: boolean;
  allowInvite?: boolean;
}

// COMPONENT
const EmailLookup = ({
  sectionTitle,
  initialValue,
  onSelect,
  resetValue = false,
  showPhantomUsers = false,
  allowInvite = false,
}: EmailLookupProps): React.JSX.Element => {
  const initialRender = useRef(true);
  const [value, setValue] = useState<string | undefined>(initialValue);
  const [items, setItems] = useState<EmailLookupItem[]>([]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    fetchListDebounced({ value: value ?? '', showPhantomUsers, setItems, allowInvite });
  }, [value]);

  return (
    <ModalInputSection
      sectionTitle={sectionTitle}
      inputs={[
        {
          Component: Input,
          typeahead: true,
          value,
          items,
          initialValue,
          placeholder: 'Email Address',
          id: 'email',
          getItemValue: (item: EmailLookupItem) => item.primaryEmail,
          getItemLabel: (item: EmailLookupItem) => item.label || item.primaryEmail,
          onChange: setValue,
          onSelect: (e: string) => {
            setValue(resetValue ? '' : e);
            const selectedItem = items.filter((item) => item.primaryEmail === e);
            setItems(selectedItem);
            onSelect(selectedItem[0]);
          },
        },
      ]}
    />
  );
};

export default EmailLookup;
